import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Cell, Row } from "react-table";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractManageList, ContractSearchParams, ContractStep, searchStatusOption } from "src/api/contract/contract-types";
import useApiOperation from "src/api/hooks/api-operation";
import { BaseCheckbox, BaseInput, BaseTooltip } from "src/components";
import { MultiSelectFilter } from "src/components/multiSelect";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import ContractManageIdLinkCell from "src/components/contract/ContractManageIdLinkCell";
import { BaseContractStatus } from "src/components/BaseContractStatus";
import { SelectAllHeaderCheckbox } from "src/components/table/SelectAllHeaderCheckbox";
import TableSelectModal from "src/components/TableSelectModal";
import { useSelectAllColumn } from "src/hooks/table/useSelectAllColumn";
import { getContractDetailPath } from "src/utils/route-util";

type ContractRow = ContractManageList & { __disabled: boolean; checked: boolean };

interface Props {
  title?: string;
  description?: string;
  onClosed: () => void;
  onClick: (contracts: ContractManageList[]) => void;
  existContractManageIds: string[];
  contractStepFilter?: ContractStep[];
  providerIds?: string;
  /** 상품 타입 필터 (예: FULL_COURT = 공간임대) */
  spaceProductType?: string;
  /** 모달 오픈 시 이미 선택되어 있는(체크 + 토글 가능) 계약 목록 */
  defaultSelectedContracts?: ContractManageList[];
  /** 계약 ID 대신 계약관리 ID를 표기한다. 이용안내처럼 계약관리 단위 데이터를 연결하는 화면에서 사용 */
  isContractManageIdColumn?: boolean;
}

interface CellProps extends Cell<ContractManageList & { __disabled: boolean; checked: boolean }> {
  checked: boolean;
}

interface SearchFormValues {
  searchValue: string;
  contractStep?: string;
}

const CONTRACT_ID_COLUMN = {
  Header: "계약 ID",
  accessor: "contractId",
  width: 80,
  Cell: (props: CellProps) => (
    <BaseNewTabLink path={getContractDetailPath({ contractId: props.value })} value={props.value} className="w-100" />
  ),
};

// 계약관리 ID 자체로는 이동할 계약을 정할 수 없어 대표 계약(계약상태 '이용중' 우선, 없으면 최신) 상세를 새 탭으로 연다
const CONTRACT_MANAGE_ID_COLUMN = {
  Header: "계약관리 ID",
  accessor: "contractManageId",
  width: 120,
  Cell: ({ data, row }: any) => (
    <ContractManageIdLinkCell
      contractManageId={row.original.contractManageId}
      contractManageIdsInPage={data.map((item: ContractManageList) => item.contractManageId)}
    />
  ),
};

const CONTRACT_REST_COLUMNS = [
  {
    Header: "상품명",
    accessor: "spaceProductName",
    width: 225,
    Cell: (props: CellProps) => (
      <div className="w-100">
        <BaseTooltip contents={props.value ? props.value : "-"}></BaseTooltip>
      </div>
    ),
  },
  {
    Header: "법인명/상호",
    accessor: "mbOrganizationName",
    width: 175,
    Cell: (props: CellProps) => (
      <div className="w-100">
        <BaseTooltip contents={props.value ? props.value : "-"}></BaseTooltip>
      </div>
    ),
  },
  {
    Header: "계약자명",
    accessor: "contractorName",
    width: 100,
    Cell: (props: CellProps) => (
      <div className="w-100">
        <BaseTooltip contents={props.value ? props.value : "-"}></BaseTooltip>
      </div>
    ),
  },
  {
    Header: "상태",
    accessor: "contractStep",
    width: 80,
    Cell: (props: CellProps) => (
      <div className="w-100 flex-center-center">
        <BaseContractStatus status={props.value} />
      </div>
    ),
  },
];

function SearchContractPopup(props: Props) {
  const {
    title = "신청/계약",
    description,
    onClosed: onCanceled,
    onClick: onAdded,
    existContractManageIds,
    contractStepFilter,
    providerIds,
    spaceProductType,
    defaultSelectedContracts,
    isContractManageIdColumn,
  } = props || {};
  const [page, setPage] = useState({ current: 0, total: 0, totalElements: 0 });
  const [currentSize, setCurrentSize] = useState(20);
  const [contractList, setContractList] = useState<ContractManageList[]>([]);
  const [selectedContractList, setSelectedContractList] = useState<ContractManageList[]>(defaultSelectedContracts ?? []);
  const { handleSubmit, getValues, control, watch } = useForm<SearchFormValues>({
    defaultValues: {
      contractStep: contractStepFilter?.join(",") || "",
    },
  });

  const contractStep = watch("contractStep");

  const searchFormRef = useRef<HTMLFormElement>(null);

  const contractListTableData = contractList.map((contract) => {
    const selectedContractManageIds = selectedContractList.map((contract) => contract.contractManageId);

    const disabled = existContractManageIds.includes(String(contract.contractManageId));
    const checked = selectedContractManageIds.includes(contract.contractManageId) || disabled;

    return {
      __disabled: disabled,
      checked,
      ...contract,
      contractStepFilter,
    };
  });

  // 전체선택 헤더 체크박스 로직 (선택 상태는 이 모달이 소유, useSelectAllColumn 훅에 콜백 주입)
  const selectedContractManageIdSet = useMemo(() => new Set(selectedContractList.map((contract) => contract.contractManageId)), [selectedContractList]);

  const { getHeaderChecked, handleToggleAll } = useSelectAllColumn<ContractRow>({
    isRowDisabled: (item) => existContractManageIds.includes(String(item.contractManageId)),
    isRowSelected: (item) => selectedContractManageIdSet.has(item.contractManageId),
    onToggleAll: (enabledItems, checked) => {
      setSelectedContractList((prev) => {
        if (checked) {
          const existingIds = new Set(prev.map((contract) => contract.contractManageId));
          const toAdd = enabledItems.filter((item) => !existingIds.has(item.contractManageId));
          return [...prev, ...toAdd];
        }
        const removeIds = new Set(enabledItems.map((item) => item.contractManageId));
        return prev.filter((contract) => !removeIds.has(contract.contractManageId));
      });
    },
  });

  const columns = useMemo(
    () => [
      {
        Header: (table: { rows: Row<ContractRow>[] }) => (
          <SelectAllHeaderCheckbox rows={table.rows} getHeaderChecked={getHeaderChecked} onToggleAll={handleToggleAll} />
        ),
        accessor: "checked",
        width: 80,
        Cell: (props: CellProps) => {
          const contractManageId = props.row.original.contractManageId;
          const disabled = props.row.original.__disabled;
          return (
            <div data-data-id={contractManageId} data-checked={props.value} data-disabled={disabled} className="checkbox">
              <BaseCheckbox id={""} name={""} checked={props.value} disabled={disabled} />
            </div>
          );
        },
      },
      isContractManageIdColumn ? CONTRACT_MANAGE_ID_COLUMN : CONTRACT_ID_COLUMN,
      ...CONTRACT_REST_COLUMNS,
    ],
    [getHeaderChecked, handleToggleAll, isContractManageIdColumn],
  );

  const { executeAsync: getContractApplyAsync } = useApiOperation(getContractApply);

  const onSubmit = (item: SearchFormValues) => {
    fetchContractList({
      page: 0,
      searchValue: getValues("searchValue"),
      contractStep: getValues("contractStep"),
      size: currentSize,
    });
  };

  const goPage = (nextPage: number) =>
    fetchContractList({ page: nextPage, searchValue: getValues("searchValue"), contractStep: getValues("contractStep"), size: currentSize });

  const fetchContractList = async ({ searchValue, contractStep, page, size }: ContractSearchParams) => {
    const result = await getContractApplyAsync({
      page,
      searchValue: searchValue,
      size: size || currentSize,
      contractStep,
      sort: {
        orders: [
          {
            property: "contractManageId",
            direction: "DESC",
          },
        ],
      },
      ...(providerIds && {
        providerIds,
      }),
      ...(spaceProductType && {
        spaceProductType,
      }),
    });

    setContractList(result.data.data.content);

    setPage({
      current: result.data.meta.pageMeta?.pageRequest.page || 0,
      total: result.data.meta.pageMeta?.totalPages || 0,
      totalElements: result.data.meta.pageMeta?.totalElements || 0,
    });

    setCurrentSize(result.data.meta.pageMeta?.pageRequest.size || 20);
  };

  const selectContract = (contractManageId: string) => {
    const selectedContract = contractList.find((contract) => String(contract.contractManageId) === contractManageId);

    if (selectedContract) {
      setSelectedContractList((prev) => [...prev, selectedContract]);
    }
  };

  const unSelectContract = (contractManageId: string) => {
    const filteredContractList = selectedContractList.filter((contract) => String(contract.contractManageId) !== contractManageId);
    setSelectedContractList(filteredContractList);
  };

  const handleSearchClick = () => {
    searchFormRef.current?.dispatchEvent(
      new Event("submit", {
        bubbles: true,
      }),
    );
  };

  const contractSteps = useMemo(() => {
    if (!contractStep) {
      return [];
    }
    return contractStep.split(",");
  }, [contractStep]);

  useEffect(() => {
    fetchContractList({
      page: 0,
      contractStep: contractStepFilter?.join(",") || "",
      searchValue: "",
    });
  }, []);

  return (
    <TableSelectModal
      onCanceled={() => onCanceled?.()}
      onAdded={() => onAdded?.(selectedContractList)}
      goPage={goPage}
      page={page}
      currentSize={currentSize}
      sizeOption={(value) => {
        setCurrentSize(value);
        fetchContractList({ page: 0, searchValue: getValues("searchValue"), contractStep: getValues("contractStep"), size: value });
      }}
      columns={columns}
      items={contractListTableData}
      title={title}
      description={description}
      select={selectContract}
      unSelect={unSelectContract}
      titleFormRender={() => {
        return (
          <form onSubmit={handleSubmit(onSubmit)} ref={searchFormRef} className="flex-center-center gap10">
            <div className="minmax140 ">
              <Controller
                name="contractStep"
                control={control}
                render={({ field }) => (
                  <MultiSelectFilter
                    placeholder="계약상태"
                    options={searchStatusOption}
                    value={contractSteps || []}
                    onChange={(values) => {
                      field.onChange(values.join(","));
                      handleSearchClick();
                    }}
                  />
                )}
              />
            </div>

            <Controller
              name="searchValue"
              control={control}
              render={({ field }) => (
                <BaseInput
                  placeholder="검색어를 입력하세요"
                  value={field.value}
                  onChange={field.onChange}
                  onSearchClick={handleSearchClick}
                  onKeyUp={handleSearchClick}
                  clearable
                />
              )}
            />
          </form>
        );
      }}
    />
  );
}
export default SearchContractPopup;
